import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { mappedAtColumn, statusColumn } from './sharedColumns';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { getActionComponent } from '../actions';

// Shared shape for the default flow's three "scorable" tabs — In Progress /
// Passed / Failed. Same columns, same ViewScoresAction wiring (ported from
// the old RequirementPipeline.jsx's view_scores column), differing only in which
// status they represent and their empty-state copy. Each tab file
// (InProgressTab.jsx, PassedTab.jsx, FailedTab.jsx) calls this once with its
// own status and registers the result — keeps three near-identical files
// from drifting out of sync with each other.
//
// Each row's own kebab menu (RowActionsCell) resolves its manual_actions
// from `manualActionsByStatus[row.status]` — that candidate's own real
// status — falling back to the tab-level `manualActions` only if their
// status isn't in the map, same as seniorFlowTabFactory.jsx, in case a
// merged tab is ever added to this flow too. For the same reason, a Status
// column is added whenever `selectable` is false (AssignmentPipeline.jsx's
// "All statuses" picked on a tab that merges more than one) — this flow
// has no merged tab today so it's a no-op in practice, kept for parity so
// one isn't quietly missing if that ever changes.
const ViewScoresAction = getActionComponent('default', 'view_scores');

export const createScorableTab = (status, emptyDescription) => {
  const ScorableTab = ({
    candidates,
    isLoading,
    page,
    pageSize,
    totalPages,
    totalItems,
    onPageChange,
    onPageSizeChange,
    opportunityId,
    assignmentId,
    requirementLocationId,
    stateMachineKey,
    manualActions,
    manualActionsByStatus,
    selectable,
    onActionSuccess,
    onViewProfile,
  }) => {
    const columns = useMemo(
      () => [
        candidateCellColumn,
        mappedAtColumn,
        ...(selectable ? [] : [statusColumn]),
        {
          key: 'view_scores',
          header: '',
          className: 'text-sm text-center',
          render: (_v, row) =>
            ViewScoresAction ? (
              <ViewScoresAction
                candidate={row}
                opportunityId={opportunityId}
                status={status}
                assignmentId={assignmentId}
                requirementLocationId={requirementLocationId}
              />
            ) : null,
        },
        {
          key: 'row_actions',
          header: '',
          className: 'text-sm',
          render: (_v, row) => (
            <RowActionsCell
              candidateId={row.candidate_id}
              mappingId={row.id}
              status={status}
              opportunityId={opportunityId}
              assignmentId={assignmentId}
              requirementLocationId={requirementLocationId}
              stateMachineKey={stateMachineKey}
              manualActions={manualActionsByStatus[row.status] || manualActions}
              onActionSuccess={onActionSuccess}
              onViewProfile={onViewProfile}
            />
          ),
        },
      ],
      [
        selectable,
        opportunityId,
        assignmentId,
        requirementLocationId,
        stateMachineKey,
        manualActions,
        manualActionsByStatus,
        onActionSuccess,
        onViewProfile,
      ]
    );

    return (
      <PipelineDataTable
        columns={columns}
        candidates={candidates}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        emptyState={<TabEmptyState description={emptyDescription} />}
      />
    );
  };

  ScorableTab.propTypes = {
    candidates: PropTypes.arrayOf(candidateShape),
    isLoading: PropTypes.bool,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requirementLocationId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    stateMachineKey: PropTypes.string,
    manualActions: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
    ),
    manualActionsByStatus: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
      )
    ),
    selectable: PropTypes.bool,
    onActionSuccess: PropTypes.func,
    onViewProfile: PropTypes.func,
  };

  ScorableTab.defaultProps = {
    candidates: [],
    isLoading: false,
    page: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
    onPageChange: () => {},
    onPageSizeChange: () => {},
    opportunityId: undefined,
    assignmentId: undefined,
    requirementLocationId: undefined,
    stateMachineKey: 'default',
    manualActions: [],
    manualActionsByStatus: {},
    selectable: true,
    onActionSuccess: () => {},
    onViewProfile: () => {},
  };

  return ScorableTab;
};
